import { submissionIdOf } from "pages/StartLead/awaitingConfirmation";

/** The zip a handoff names. `ped` is either the legacy base64 `{zip}` or a
 * base64url signed payload whose JSON carries `zip`; anything else yields
 * null. Never throws. */
export function zipFromPed(ped: string | null): string | null {
  if (!ped) return null;
  try {
    const base64 = ped.replace(/-/g, "+").replace(/_/g, "/");
    const json: unknown = JSON.parse(atob(base64));
    if (typeof json !== "object" || json === null) return null;
    const zip = (json as Record<string, unknown>).zip;
    if (typeof zip === "string" && zip.trim() !== "") return zip;
    if (typeof zip === "number" && Number.isFinite(zip)) return String(zip);
    return null;
  } catch {
    return null;
  }
}

export type SignedHandoff = {
  eventId: string;
  ped: string;
  sig: string;
  exp: string;
};

/** The signed handoff in the query, present only when every part is. */
export function signedHandoffOf(params: URLSearchParams): SignedHandoff | null {
  const eventId = params.get("event_id");
  const ped = params.get("ped");
  const sig = params.get("sig");
  const exp = params.get("exp");
  return eventId && ped && sig && exp ? { eventId, ped, sig, exp } : null;
}

/** `none` means no submission is held yet and may still arrive; `settled`
 * is final (confirmed, malformed, or the request failed). */
export type HandoffStatus =
  | { kind: "awaiting_confirmation"; submissionId: string }
  | { kind: "none" }
  | { kind: "settled" };

/** The handoff-status answer. Only a well-formed awaiting-confirmation
 * answer interrupts the page; `none` is worth asking again; anything else
 * leaves the page as it is. */
export function parseHandoffStatus(raw: unknown): HandoffStatus {
  if (typeof raw === "object" && raw !== null) {
    const submissionId = submissionIdOf(raw);
    const status = (raw as Record<string, unknown>).status;
    if (status === "awaiting_confirmation" && submissionId) {
      return { kind: "awaiting_confirmation", submissionId };
    }
    if (status === "none") return { kind: "none" };
  }
  return { kind: "settled" };
}
